import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { statisticsApi } from '../services/statisticsApi';
import type { PlayerGlobalStats } from '../services/statisticsApi';
import './PersistentHeader.css';

/**
 * PersistentHeader - Header that persists across all pages (except game room)
 * Shows user info, level (calculated from stats), and logout button
 */
const PersistentHeader: React.FC = () => {
  const { user, logout } = useAuth();
  const [, setUserStats] = useState<PlayerGlobalStats | null>(null);
  const [levelInfo, setLevelInfo] = useState({ level: 1, xp: 0, nextLevelXp: 100, progress: 0 });

  useEffect(() => {
    loadUserStats();
  }, [user]);

  const loadUserStats = async () => {
    const userId = user?.id || user?._id;
    if (!userId) return;

    try {
      const globalStats = await statisticsApi.getPlayerGlobalStats(userId);
      setUserStats(globalStats);
      const calculated = statisticsApi.calculatePlayerLevel(globalStats);
      setLevelInfo(calculated);
    } catch (error) {
      console.error('Error loading user stats for header:', error);
      // Fallback to user.level if stats fail to load
      setLevelInfo({ level: user?.level || 1, xp: 0, nextLevelXp: 100, progress: 0 });
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  return (
    <header className="persistent-header">
      <div className="header-container">
        {/* Logo - Card Game Platform Design */}
        <div className="header-logo">
          <span className="logo-icon">
            <img src="/joker-card.svg" alt="Card" style={{ width: '28px', height: '28px' }} />
          </span>
          <span className="logo-text">Card Game Platform</span>
        </div>

        {/* User Info */}
        <div className="header-user">
          <div className="user-avatar">
            {(user?.username || 'U').charAt(0).toUpperCase()}
          </div>
          <div className="user-details">
            <div className="user-name">{user?.username || 'Player'}</div>
            <div className="user-level">Level {levelInfo.level}</div>
          </div>
          
          {/* Logout Button */}
          <button onClick={handleLogout} className="header-logout-btn" title="Logout">
            <span className="logout-icon">🚪</span>
            <span className="logout-text">Logout</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default PersistentHeader;
